import React, { useMemo } from 'react';
import TeamJersey from './TeamJersey';
import QuestionCard from './QuestionCard';
import { OptionPercentageStat } from './OptionPercentageStat';
import { LockBadge } from './shared/LockBadge';
import { createJerseyMap } from '../utils/jersey-resolver';
import { isOrphanCard, ORPHAN_CARD_CLASSES } from './orphan-card';
import {
  getPickOptionTextClass,
  getPickResultSurfaceClass,
  hasPickResult,
} from './shared/pickResultStyles';
import {
  getPickLockedBackgroundClass,
  getPickLockedBorderClass,
  getPickLockedLabelClass,
  getPickLockedSecondaryClass,
} from './shared/pickLockedStyles';

interface PlayerPickProps {
  options: any[];
  selectedTeam?: string;
  onTeamSelect: (teamId: string | undefined) => void;
  points: number;
  earnedPoints?: number;
  questionText?: string;
  subtext?: string;
  isCorrect?: boolean | null;
  disabled?: boolean;
  /** Contest locked — same chrome as team locked (Figma 10203:54636). */
  locked?: boolean;
  className?: string;
  questionId?: string;
  questionNumber?: number;
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

function getSecondaryTextClass(isSelected: boolean, hasResult: boolean, locked: boolean): string {
  if (locked) return getPickLockedSecondaryClass();
  if (isSelected && !hasResult) return 'text-surface-muted';
  if (hasResult) return 'text-white/80';
  return 'text-muted-foreground';
}

/**
 * PlayerPick - Component for option selection questions
 * Used when users need to select a option (e.g., "Who will score first goal?")
 * Figma CD — x4 pick (10203:60513)
 */
const PlayerPick: React.FC<PlayerPickProps> = ({
  options,
  selectedTeam,
  onTeamSelect,
  points,
  earnedPoints,
  questionText = 'Select a option',
  subtext,
  isCorrect,
  disabled = false,
  locked = false,
  className = '',
  questionId,
  questionNumber,
}) => {
  // Memoize team jersey data to prevent random changes and unnecessary re-renders
  const teamJerseyMap = useMemo(() => createJerseyMap(options), [options]);

  // Daily Double chrome is unused for Circus Daily — ignore metadata styling.
  const isDailyDouble = false;

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      className={className}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={questionId ? `question-${questionId}` : 'option-pick'}
      data-testtype="option"
    >
      {/* Subtext (if present) */}
      {subtext && (
        <span
          data-testid="option-subtext"
          className="text-muted-foreground font-sans font-normal block text-center mt-2 text-sm"
        >
          {subtext}
        </span>
      )}

      <span
        data-testid="option-question"
        className="text-white font-sans font-normal block text-center mb-4 text-sm leading-5"
      >
        {questionText}
      </span>

      <div
        className={`grid gap-3 ${options.length > 4 ? 'grid-cols-1' : 'grid-cols-2'}`}
        data-testid="option-selection"
      >
        {options.map((option, index) => {
          const isSelected = selectedTeam === option.id;
          const hasResult = hasPickResult(isCorrect);
          const resultSurfaceClass = getPickResultSurfaceClass(isSelected, isCorrect);

          // Single column layout (more than 4 options)
          const isSingleColumn = options.length > 4;
          const isOrphan = isOrphanCard({ index, total: options.length, isSingleColumn });

          const getBackgroundClass = () => {
            if (resultSurfaceClass) return '';
            if (locked) return getPickLockedBackgroundClass(isSelected);
            if (isSelected) return 'bg-white';
            return 'bg-surface-muted';
          };

          const getBorderClass = () => {
            if (resultSurfaceClass) return resultSurfaceClass;
            if (locked) return getPickLockedBorderClass(isSelected);
            if (isSelected) return 'border border-white';
            return 'border border-transparent';
          };

          const getHoverClass = () => {
            if (disabled || hasResult || locked) return '';
            return isSelected ? '' : 'hover:brightness-110';
          };

          const labelClass =
            locked && isSelected
              ? getPickLockedLabelClass()
              : getPickOptionTextClass(isSelected, hasResult);
          const secondaryClass = getSecondaryTextClass(isSelected, hasResult, locked);
          const jerseyUrl = teamJerseyMap.get(option.id);
          const showLockBadge = locked && isSelected && !hasResult;

          return (
            <div
              key={option.id}
              className={`flex flex-col items-center justify-center ${
                isOrphan ? ORPHAN_CARD_CLASSES : ''
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  if (!disabled && !locked) onTeamSelect(isSelected ? undefined : option.id);
                }}
                disabled={disabled || locked}
                data-testid={`option-${option.id}`}
                data-testresult={
                  isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined
                }
                data-selected={isSelected ? 'true' : 'false'}
                data-locked={locked ? 'true' : undefined}
                className={`
                  group peer w-full h-full relative overflow-hidden rounded-xl transition-smooth
                  ${isSingleColumn ? 'min-h-[80px] grid grid-cols-[auto_1fr_auto] gap-3 items-stretch p-0' : 'flex flex-col h-[168px]'}
                  ${getBackgroundClass()}
                  ${getBorderClass()}
                  ${getHoverClass()}
                  ${disabled || locked ? 'cursor-not-allowed' : ''}
                  ${disabled && !hasResult && !locked ? 'opacity-90' : ''}
                `}
              >
                {isSingleColumn ? (
                  <>
                    {/* Left: player image */}
                    <div className="relative flex items-end justify-center w-[72px] min-h-[80px] overflow-hidden">
                      {option.popularityPct !== undefined && options.length >= 2 && (
                        <div className="absolute top-1.5 left-1.5 z-10">
                          <OptionPercentageStat
                            percentage={option.popularityPct}
                            isSelected={isSelected && !hasResult && !locked}
                          />
                        </div>
                      )}
                      <TeamJersey size="sm" jerseyUrl={jerseyUrl} />
                      {showLockBadge && (
                        <LockBadge
                          data-testid={`lock-badge-${option.id}`}
                          className="absolute bottom-1 left-1/2 z-10 -translate-x-1/2"
                        />
                      )}
                    </div>

                    {/* Middle: name + position (+ optional fixture) */}
                    <div className="flex flex-col justify-center text-left min-w-0 py-2 pr-1">
                      <div className="flex items-center gap-1 min-w-0">
                        <span
                          className={`text-sm font-sans font-semibold capitalize truncate ${labelClass}`}
                        >
                          {option.name}
                        </span>
                      </div>
                      {option.position && (
                        <span className={`text-sm font-sans capitalize ${secondaryClass}`}>
                          {option.position}
                        </span>
                      )}
                      {option.fixture && (
                        <div className="text-xs mt-0.5">
                          <span
                            className={
                              option.isPlayerTeam === 'away' ? labelClass : secondaryClass
                            }
                          >
                            {option.fixture.awayTeamAbbreviation}
                          </span>
                          <span className={secondaryClass}> @ </span>
                          <span
                            className={
                              option.isPlayerTeam === 'home' ? labelClass : secondaryClass
                            }
                          >
                            {option.fixture.homeTeamAbbreviation}
                          </span>
                          {option.fixture?.matchTime && (
                            <span className={`${secondaryClass} ml-2`}>
                              {option.fixture.matchTime}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right: team logo */}
                    {option.thirdImageUrl && (
                      <div className="flex items-center justify-center pr-2">
                        <img
                          src={option.thirdImageUrl}
                          alt="Team Logo"
                          className="size-10 object-contain"
                          onError={e => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Image area — Figma: mugshot, vote %, team logo */}
                    <div className="relative flex-1 min-h-29 w-full">
                      {option.popularityPct !== undefined && options.length >= 2 && (
                        <div className="absolute top-1.5 left-1.5 z-10 w-7">
                          <OptionPercentageStat
                            percentage={option.popularityPct}
                            isSelected={isSelected && !hasResult && !locked}
                          />
                        </div>
                      )}

                      {option.thirdImageUrl && (
                        <div className="absolute top-1.5 right-1 z-10 size-10 flex items-center justify-center">
                          <img
                            src={option.thirdImageUrl}
                            alt="Team Logo"
                            className="size-full object-contain"
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      <div className="relative inset-x-0 bottom-0 flex justify-center overflow-hidden h-[115px]">
                        {jerseyUrl ? (
                          <img
                            src={jerseyUrl}
                            alt={option.name}
                            className="h-full w-auto max-w-[140px] object-contain object-bottom"
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : null}
                        {showLockBadge && (
                          <LockBadge
                            data-testid={`lock-badge-${option.id}`}
                            className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2"
                          />
                        )}
                      </div>
                    </div>

                    {/* Player detail — name + position */}
                    <div
                      className={`border-t-2 flex flex-col gap-1 items-center text-center capitalize px-2 pt-3 pb-3 shrink-0 w-full ${
                        locked
                          ? 'border-border'
                          : isSelected && !hasResult
                            ? 'border-[#C6C6C6]'
                            : 'border-surface'
                      }`}
                    >
                      <p
                        className={`font-sans text-label-bold truncate w-full whitespace-nowrap ${labelClass}`}
                      >
                        {option.name}
                      </p>
                      {option.position && (
                        <p className={`font-sans text-label w-full ${secondaryClass}`}>
                          {option.position}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </button>

              {/* Live Value (shown during live scoring) */}
              {option.liveValue !== undefined && (
                <div
                  data-testid={`live-value-${option.id}`}
                  className="text-sm font-medium mt-1 text-[#3bbe1e]"
                >
                  {option.liveValue}
                  {option.liveStatType ? ` ${option.liveStatType}` : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </QuestionCard>
  );
};

export default PlayerPick;
